import { PayloadAction } from '@reduxjs/toolkit';
import { of, concat, Observable } from 'rxjs';

import {
  CURRENT_BALANCE, PLACE_YOUR_BETS, NO_MORE_BETS, SHUFFLE, GAME_RESULT, CARD, RECONNECT_TO_SERVER,
} from '@ezugi/constants';
import type { SocketMessagePayload, SocketReconnectToServerMessagePayload } from '@ezugi/types';

import { state$ } from '../../../streams';
import {
  bonusActions,
  gameActions,
  roundActions,
  tipsActions,
  userActions,
} from '../../actions';
import {
  chipsSelector,
  evoWrapperBalanceSelector,
  evoWrapperBalanceVersionSelector,
  isEvoWrappedSelector,
  roundSelector,
  selectedChipSelector,
  shuffleNextRoundSelector,
  videoDelaySelector,
} from '../../selectors';
import { bonusChipsSelector, bonusSelectedChipSelector } from '../../selectors/bonus';
import { RootState } from '../../types';
import { handleReconnectToServer } from './handleReconnectToServer';

const { round } = roundActions;
const { game } = gameActions;
const { tips } = tipsActions;

export const TIP_PLACED = 'TipPlaced';
export const TIP_OUT_OF_LIMITS = 'TipOutOfLimits';

/**
 * We're subtracting the `videoDelay` from the `PLACE_YOUR_BETS` timer in order to send
 * the bets to the server when `NO_MORE_BETS` event is received
 * @param {Object} state Application state
 * @param {Number} time PLACE_YOUR_BETS time in seconds
 */
const getTimerLeftWithDelay = (state: RootState, time: number) => {
  const t = Math.floor(time - (videoDelaySelector(state)));
  return t < 0 ? 0 : t;
};

export default (socketMessage: SocketMessagePayload, state: RootState) => {
  const {
    MessageType,
    roundId,
    tvRoundNumber,
    TimerTimeLeft,
    TimerTime,
    timeStamp,
    RoundTripStartTime,
    roundStartTimestamp,
    balanceVersion: evoWalletBalanceVersion,
  } = socketMessage;

  let { balance } = socketMessage;
  const currentRound = roundSelector(state);

  switch (MessageType) {
  case CURRENT_BALANCE: {
    const currentChip = selectedChipSelector(state);
    const isEvoWrapped = isEvoWrappedSelector(state);
    const evoWrapperBalance = evoWrapperBalanceSelector(state);
    const evoWrapperBalanceVersion = evoWrapperBalanceVersionSelector(state) as number;
    const currentBonusSelectedChip = bonusSelectedChipSelector(state);
    if (!currentChip) throw new Error('Chips not configured');

    /* BalanceVersion refers to a timestamp in Unix time.
       We compare the timestamp from FE Wrapper to the one from GE (that has it from Evo Wallet)
       because both are received asynchronously and we want to
       always have latest balance
    */
    if (isEvoWrapped && evoWalletBalanceVersion) {
      balance = evoWrapperBalanceVersion > evoWalletBalanceVersion ? evoWrapperBalance : balance;
      (state$ as any).dispatch(userActions.user.set({ evoWalletBalanceVersion }));
    }

    const actions = [ of(userActions.user.set({ startBalance: balance,
      ...state.user,
      balance,
      hasInitializedBalance: true })) ] as Observable<PayloadAction<any>>[];

    // Select a lower value chip if balance dropped below currently selected chip
    if (balance < currentChip.value) {
      const chips = chipsSelector(state);
      const selectedChip = chipsSelector(state).reduce(
        (acc, curr) => (curr.value > acc.value && balance >= curr.value ? curr : acc),
        chips[0]
      );

      actions.push(of(game.set({ selectedChip })));
    }

    // Select a lower value chip if balance dropped below currently selected chip
    if (currentBonusSelectedChip && (balance < currentBonusSelectedChip.value)) {
      const bonusChips = bonusChipsSelector(state);
      const bonusSelectedChip = bonusChips.reduce(
        (acc, curr) => (curr.value > acc.value && balance >= curr.value ? curr : acc),
        bonusChips[0]
      );

      actions.push(of(bonusActions.bonus.selectChip(bonusSelectedChip)));
    }

    if (actions.length > 1) {
      return concat(...actions);
    }

    return actions[0];
  }
  case TIP_PLACED: {
    return of(tips.success());
  }
  case TIP_OUT_OF_LIMITS: {
    return of(tips.failure());
  }
  case PLACE_YOUR_BETS: {
    const _timeLeft = getTimerLeftWithDelay(state, TimerTimeLeft);
    const forceNMB = _timeLeft <= 0 && TimerTime > 0;
    // Current round has shuffle time if shuffle card was drawn in the previous round or if the user just joined an in-progress shuffle round
    const isShuffleTime = shuffleNextRoundSelector(state) || currentRound.isShuffleTime;

    return of(
      round.set({
        roundStatus: forceNMB ? NO_MORE_BETS : PLACE_YOUR_BETS,
        roundId,
        tvRoundNumber,
        timestamp: timeStamp,
        canPlaceBets: !forceNMB,
        isShuffleTime,
        ...(!forceNMB ? {
          roundTime: RoundTripStartTime,
          roundStartTimestamp,
          timeLeft: _timeLeft,
          totalTime: getTimerLeftWithDelay(state, TimerTime),
          winningNumber: null,
          winAmount: 0,
          messageTimestamp: Date.now(),
        } : {}),
      }),
      game.set({
        shuffleNextRound: false,
      })
    );
  }
  case NO_MORE_BETS: {
    return of(
      round.set({
        roundStatus: NO_MORE_BETS,
        roundId,
        timestamp: timeStamp,
        canPlaceBets: false,
      })
    );
  }
  case SHUFFLE: {
    if (currentRound.roundStatus === PLACE_YOUR_BETS) {
      return of(
        round.set({
          isShuffleTime: true,
        })
      );
    }

    return of(
      game.set({ shuffleNextRound: true })
    );
  }
  case GAME_RESULT: {
    return of(
      round.set({
        isShuffleTime: false,
        cardReceived: false,
      })
    );
  }
  case CARD: {
    return of(
      round.set({
        isShuffleTime: false,
        cardReceived: true,
      })
    );
  }
  case RECONNECT_TO_SERVER: {
    return handleReconnectToServer(socketMessage as SocketReconnectToServerMessagePayload);
  }
  default: {
    return null;
  }
  }
};
